import { useEffect, useState } from "react";
import Compressor from "compressorjs";

import { AdministrationHelper } from "@/Administration/Helpers/AdministrationHelper";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { Locale } from "@/Common/Models/Localization/Locale";
import { IClinicLogo } from "@/Common/Models/Sites/IClinicLogo";
import * as LocalOperations from "@/Common/Operations/operations.local";

import styles from "@/Administration/Components/Sites/SiteView/ImageUploadPopup.module.scss";

import LogoMyle from "@/Globals/_images/logo-myle.svg";

interface IUploadSiteLogoPopupProps {
    logoAttachments: IClinicLogo[];
    siteId: string;
    onClose: () => void;
    onUpdate: () => void;
}

interface IClinicLogoDimension {
    logoWidth: number;
    logoHeight: number;
}

interface ILogoAttachment {
    file: any;
    name: string;
    locale: Locale;
    imageSrc?: string;
    imageDimensions?: IClinicLogoDimension;
    attachmentId?: string;
    showNonSupportedFileTypeMessage?: boolean;
}

const minHeight = 80;
const minWidth = 80;
const maxHeight = 512;
const maxWidth = 512;

function UploadSiteLogoPopup(props: IUploadSiteLogoPopupProps): JSX.Element {
    const { getText } = useTranslation();
    const { siteId, logoAttachments, onClose, onUpdate } = props;
    const [attachments, setAttachments] = useState<ILogoAttachment[]>([]);
    const defaultControlId = `FileUploadLite_${sessionStore.newId()}`;
    const defaultAttachmentIndex = attachments.findIndex(item => IsNullOrUndefined(item.locale));

    const onUploadClick = (event: React.MouseEvent<HTMLInputElement, MouseEvent>): void => {
        event.currentTarget.value = "";
    };

    const onChange = (files: FileList, index: number): void => {
        const allowedExtensions = /(\.jpg|\.jpeg|\.png)$/i;
        const newAttachments = [...attachments];
        const firstFile = files[0];
        newAttachments[index].showNonSupportedFileTypeMessage = false;

        if (firstFile == null)
            return;

        if (!allowedExtensions.exec(firstFile.name)) {
            newAttachments[index].showNonSupportedFileTypeMessage = true;
            setAttachments(newAttachments);
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(firstFile);
        reader.onloadend = () => {
            const url = URL.createObjectURL(firstFile);

            newAttachments[index].imageSrc = reader.result.toString();

            applyImageDimensions(url).then(value => {
                newAttachments[index].file = firstFile;
                newAttachments[index].imageDimensions = { logoWidth: value.width, logoHeight: value.height };

                setAttachments(newAttachments);
            });
        };
    };

    const applyImageDimensions = (url: string): Promise<{ width: number, height: number }> => new Promise<{ width: number, height: number }>(resolve => {
        const img = new Image();
        img.src = url;
        img.onload = () => {
            resolve({ width: img.width, height: img.height });
        };
    });

    const resizeImage = (file: File, index: number): Promise<File> => new Promise<File>((resolve, reject) => {
        if (attachments[index].imageDimensions.logoWidth < minWidth && attachments[index].imageDimensions.logoHeight < minHeight) {
            new Compressor(file, {
                minHeight: minHeight, minWidth: minWidth, quality: 1,
                success(result: File) {
                    resolve(new File([result], file.name));
                },
                error(error: Error) {
                    reject(error);
                }
            });
        }
        else if (attachments[index].imageDimensions.logoWidth > maxWidth && attachments[index].imageDimensions.logoHeight > maxHeight) {
            new Compressor(file, {
                maxHeight: maxHeight, maxWidth: maxWidth, quality: 1,
                success(result: File) {
                    resolve(new File([result], file.name));
                },
                error(error: Error) {
                    reject(error);
                }
            });
        }
        else {
            resolve(file);
        }
    });

    const fileReader = (file: File): Promise<string> => new Promise<string>(resolve => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
            resolve(reader.result.toString());
        };
    });

    const applyClinicLogo = async (siteId: string, forAllSites: boolean): Promise<void> => {
        const changedAttachments = [...attachments];

        for (let index = 0; index < changedAttachments.length; index++) {
            const item = changedAttachments[index];

            if (item.file) {
                const newFile = await resizeImage(item.file, index);
                const value = await fileReader(newFile);
                changedAttachments[index].name = changedAttachments[index].file.name;
                changedAttachments[index].file = value;
            }
        }

        const response = changedAttachments?.filter(item => !(!item.locale && IsNullOrUndefined(item.attachmentId) && IsNullOrUndefined(item.file))).map(item => ({
            language: item.locale,
            attachmentId: item.file ? null : item.attachmentId,
            file: item.file,
            name: item.name
        }));

        sessionStore.dispatch(
            LocalOperations.Sites.SaveLogo,
            {
                logoAttachments: response, siteId: siteId, forAllSites: forAllSites
            }
        );

        setAttachments(attachments?.map(item => ({
            ...item,
            file: null,
            name: null
        })));
    };

    const onClickApply = async (forAllSites: boolean): Promise<void> => {
        await applyClinicLogo(siteId, forAllSites);
        onUpdate();
        onClose();
    };

    const onClickCancel = (): void => {
        onClose();
        setAttachments(attachments?.map(item => ({ ...item, file: null })));
    };

    const addTranslatedLogo = (): void => {
        const rest = [...attachments];
        const existingLanguages = attachments.map(item => item.locale);
        const availableLanguages = AdministrationHelper.orderedLocales.filter(item => !existingLanguages.includes(item as Locale));

        rest.push({
            file: null,
            name: null,
            locale: availableLanguages[0] as Locale
        });

        setAttachments(rest);
    };

    const deleteLogoSite = (index: number): void => {
        const rest = [...attachments];

        rest.splice(index, 1);

        setAttachments(rest);
    };

    const getAvailableLanguages = (index: number): ComboBoxDropDownItem[] => {
        const availableLanguages = AdministrationHelper.orderedLocales.filter(item => !attachments.find((value, i) => (value.locale === (item as Locale) && i !== index)));
        return availableLanguages.map(item => new ComboBoxDropDownItem(item.toString(), AdministrationHelper.getLanguageName(item)));
    };

    useEffect(() => {
        const newAttachments: ILogoAttachment[] = logoAttachments?.map(item => ({
            file: null,
            name: null,
            locale: item.Locale,
            imageSrc: PrintHelper.GetAttachmentPreviewPath(item.AttachmentId) + `&dummy=${sessionStore.newId()}`,
            attachmentId: item.AttachmentId,
            imageDimensions: null
        })) || [];
        const defaultAttachment = newAttachments?.find(item => IsNullOrUndefined(item.locale));

        if (!defaultAttachment) {
            newAttachments.push({
                file: null,
                name: null,
                locale: null,
                imageSrc: null,
                attachmentId: null,
                imageDimensions: null
            });
        }

        const filteredAttachments = newAttachments.filter(item => item.imageSrc && !item.imageDimensions);

        Promise.all(filteredAttachments.map(item => applyImageDimensions(item.imageSrc))).then((values: { width: number, height: number }[]) => {
            values.forEach((value, index) => {
                filteredAttachments[index].imageDimensions = { logoWidth: value.width, logoHeight: value.height };
            });

            const filteredDefaultItem = filteredAttachments.find(item => IsNullOrUndefined(item.locale));
            const combinedAttachments = filteredDefaultItem ? filteredAttachments : [newAttachments.find(item => IsNullOrUndefined(item.locale)), ...filteredAttachments];

            setAttachments(combinedAttachments);
        });
    }, [logoAttachments]);

    return (
        <Popup
            titleAlternateComponent={<h3 className="thin-font">{getText("ClinicInfo_ChangeClinicLogoLabel")}</h3>}
            cssClass="profileimage "
            draggable
            onCloseDelegate={onClose}
        >
            <div className="p-body" style={{ overflowY: "auto", maxHeight: "650px" }}>
                <div className="row no-side-margin">
                    <div className="col-lg-12">
                        <div>
                            <div id={defaultControlId}>
                                <label>{getText("ClinicInfo_ImageMinSizeLabel")}</label>
                                <label className={styles.imageValidationLabelPosition}>{getText("ClinicInfo_SupportedImageFileTypesLabel")}</label>
                                <label className={styles.imageValidationLabelFont}>{getText("ClinicInfo_DefaultLogoLabel")}</label>
                                <div className={styles.clinicInfoUploadRow}>
                                    <div className={styles.cursorPointer}>
                                        <input
                                            id={defaultControlId + "_input"}
                                            name="fn00"
                                            type="file"
                                            accept=".jpg,.jpeg,.png"
                                            className={styles.hideImageValidationLabel}
                                            onChange={e => onChange(e.currentTarget.files, defaultAttachmentIndex)}
                                            onClick={onUploadClick}
                                        />
                                        <div className="upload-progress"><div className="bar" /></div>
                                        <label className="link-label" htmlFor={defaultControlId + "_input"}>{getText("ClinicInfo_UploadDefaultLogoLabel")}</label>
                                    </div>

                                    {attachments[defaultAttachmentIndex]?.imageSrc && (
                                        <div className={styles.cursorPointer}>
                                            <label
                                                className={`${styles.imageDeleteLabel}`}
                                                onClick={() => deleteLogoSite(defaultAttachmentIndex)}
                                            >
                                                {getText("DeleteLabel")}
                                            </label>
                                        </div>
                                    )}

                                </div>
                                {attachments[defaultAttachmentIndex]?.showNonSupportedFileTypeMessage && <label className={styles.imageValidationLabelColor}>{getText("ClinicInfo_FileTypeNotSupportedLabel")}</label>}
                            </div>
                            <div className={styles.clinicModalLogoWrapper}>
                                {attachments[defaultAttachmentIndex]?.imageSrc ? (
                                    <img
                                        src={attachments[defaultAttachmentIndex]?.imageSrc}
                                        className={styles.clinicModalLogo}
                                        style={{ height: attachments[defaultAttachmentIndex]?.imageDimensions?.logoWidth > minWidth || attachments[defaultAttachmentIndex]?.imageDimensions?.logoHeight > minHeight ? "100%" : `${minHeight}px` }}
                                    />
                                ) :
                                    <LogoMyle className={styles.clinicModalLogo} style={{ height: attachments[defaultAttachmentIndex]?.imageDimensions?.logoWidth > minWidth || attachments[defaultAttachmentIndex]?.imageDimensions?.logoHeight > minHeight ? "100%" : `${minHeight}px` }} />
                                }
                            </div>
                        </div>

                        {attachments?.map((item, index) => {
                            if (IsNullOrUndefined(item.locale)) {
                                return;
                            }

                            const isImageLarger = attachments[index].imageDimensions?.logoWidth > minWidth || attachments[index]?.imageDimensions?.logoHeight > minHeight;
                            const controlId = `FileUploadLite_${sessionStore.newId()}`;

                            return (
                                <div key={`logo_${index}`}>
                                    <div id={controlId}>
                                        <label style={{ fontWeight: 700 }}>{getText("ClinicInfo_TranslatedLogoLanguageLabel")}</label>
                                        <SelectLookupControl
                                            IncludeEmptyChoice={false}
                                            Value={item.locale}
                                            Callback={e => {
                                                const rest = [...attachments];
                                                rest[index].locale = e;
                                                rest[index].showNonSupportedFileTypeMessage = false;
                                                setAttachments(rest);
                                            }}
                                            AvailableValues={getAvailableLanguages(index)}
                                        />
                                        <div className={styles.clinicInfoUploadRow}>
                                            <div className={styles.cursorPointer}>
                                                <input
                                                    id={controlId + "_input"}
                                                    name="fn00"
                                                    type="file"
                                                    accept=".jpg,.jpeg,.png"
                                                    className={styles.hideImageValidationLabel}
                                                    onChange={e => onChange(e.currentTarget.files, index)}
                                                    onClick={onUploadClick}
                                                />
                                                <div className="upload-progress"><div className="bar" /></div>
                                                <label className="link-label" htmlFor={controlId + "_input"}>{getText("ClinicInfo_UploadTranslatedLogoLabel")}</label>
                                            </div>
                                            <div className={styles.cursorPointer}>
                                                <label className={`${styles.imageDeleteLabel}`} onClick={() => deleteLogoSite(index)}>{getText("ClinicInfo_DeleteTranslatedLogoLabel")}</label>
                                            </div>
                                        </div>
                                        {attachments[index]?.showNonSupportedFileTypeMessage && <label className={styles.imageValidationLabelColor}>{getText("ClinicInfo_FileTypeNotSupportedLabel")}</label>}
                                    </div>
                                    <div className={styles.clinicModalLogoWrapper}>
                                        {item?.imageSrc ? (
                                            <img
                                                src={item?.imageSrc}
                                                className={styles.clinicModalLogo}
                                                style={{ height: isImageLarger ? "100%" : `${minHeight}px` }}
                                            />
                                        )
                                            : (
                                                <LogoMyle
                                                    className={styles.clinicModalLogo}
                                                    style={{ height: isImageLarger ? "100%" : `${minHeight}px` }}
                                                />
                                            )
                                        }
                                    </div>
                                </div>
                            );
                        })}

                        {attachments.length <= EnumHelper.getNames(Locale).length &&
                            <label className={`link-label ${styles.cursorPointer}`} onClick={addTranslatedLogo}>{getText("ClinicInfo_AddTranslatedLogoLabel")}</label>
                        }
                    </div>
                </div>
            </div>

            <div className="p-footer flex-display flex-end">
                <ButtonGroup>
                    <ButtonControl
                        className="btn btn-default"
                        onClick={() => onClickCancel()}
                        label={getText("CancelLabel")}
                    />
                    <ButtonControl
                        className="btn btn-primary"
                        onClick={() => onClickApply(true)}
                        label={getText("ClinicInfo_ApplyToAllSitesLabel")}
                    />
                    <ButtonControl
                        className="btn btn-primary"
                        onClick={() => onClickApply(false)}
                        label={getText("ClinicInfo_ApplyLabel")}
                    />
                </ButtonGroup>
            </div>
        </Popup>
    );
}

export { IClinicLogoDimension };
export default UploadSiteLogoPopup;
